import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, track } from "@/lib/api";
import CTAButtons from "@/components/CTAButtons";
import { Card } from "@/components/ui/card";
import { Check, X, MapPin, Calendar, Hotel, Car } from "lucide-react";

export default function PackageDetail() {
  const { slug } = useParams();
  const { data: p, isLoading } = useQuery({
    queryKey: ["pkg", slug],
    queryFn: () => api.get(`/packages/${slug}`).then((r) => r.data),
  });
  const { data: related = [] } = useQuery({
    queryKey: ["pkg-related"],
    queryFn: () => api.get("/packages", { params: { is_active: true, limit: 4 } }).then((r) => r.data.items),
  });
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => { if (slug) track("package_view", slug); }, [slug]);

  if (isLoading || !p) return <div className="min-h-[60vh] pt-32 text-center text-slate-500">Loading…</div>;

  return (
    <div className="pb-24">
      <div className="relative h-[70vh] min-h-[420px] w-full overflow-hidden" data-testid="pkg-banner">
        <img src={p.banner_url} alt={p.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />
        <div className="absolute inset-x-0 bottom-0 max-w-7xl mx-auto px-6 lg:px-10 pb-16 text-white">
          <div className="text-xs uppercase tracking-[0.24em] text-amber-300 mb-3">{p.destination}</div>
          <h1 className="font-display text-5xl sm:text-6xl">{p.name}</h1>
          <div className="mt-4 flex flex-wrap gap-6 text-sm text-white/85">
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4" /> {p.duration_days} Days · {p.duration_nights} Nights</span>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> {p.destination}</span>
            <span>From <b className="text-amber-300">₹{p.price_from?.toLocaleString?.("en-IN")}</b></span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 mt-16">
        <div className="lg:col-span-8 space-y-14">
          <section>
            <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-3">Overview</div>
            <p className="text-slate-700 text-lg leading-relaxed">{p.overview}</p>
          </section>

          {p.highlights?.length > 0 && (
            <section>
              <h2 className="font-display text-3xl text-slate-950 mb-6">Highlights</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {p.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200"><Check className="w-4 h-4 mt-1 text-emerald-600" /> <span className="text-slate-700">{h}</span></div>
                ))}
              </div>
            </section>
          )}

          {p.gallery?.length > 0 && (
            <section>
              <h2 className="font-display text-3xl text-slate-950 mb-6">Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {p.gallery.map((src, i) => (
                  <button key={i} onClick={() => setLightbox(src)} className="rounded-xl overflow-hidden aspect-square" data-testid={`pkg-gallery-${i}`}>
                    <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </button>
                ))}
              </div>
            </section>
          )}

          {p.itinerary?.length > 0 && (
            <section>
              <h2 className="font-display text-3xl text-slate-950 mb-6">Day-wise itinerary</h2>
              <ol className="relative border-l-2 border-amber-300 pl-6 space-y-6">
                {p.itinerary.map((d, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[35px] top-0 w-6 h-6 rounded-full bg-amber-400 grid place-items-center text-[11px] font-bold text-slate-950">{d.day}</span>
                    <div className="font-display text-xl text-slate-950">Day {d.day} — {d.title}</div>
                    <p className="text-slate-600 mt-1 leading-relaxed">{d.details}</p>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <section className="grid md:grid-cols-2 gap-6">
            <Card className="p-6"><div className="flex items-center gap-2 text-amber-700 mb-3"><Car className="w-4 h-4" /> <span className="text-xs uppercase tracking-[0.2em]">Vehicle</span></div><div className="text-slate-700">{p.vehicle_info}</div></Card>
            <Card className="p-6"><div className="flex items-center gap-2 text-amber-700 mb-3"><Hotel className="w-4 h-4" /> <span className="text-xs uppercase tracking-[0.2em]">Hotels</span></div><div className="text-slate-700">{p.hotel_info}</div></Card>
          </section>

          <section className="grid md:grid-cols-2 gap-6">
            {p.inclusions?.length > 0 && (
              <div>
                <h3 className="font-display text-2xl text-slate-950 mb-4">Inclusions</h3>
                <ul className="space-y-2">
                  {p.inclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-slate-700"><Check className="w-4 h-4 mt-1 text-emerald-600" /> {x}</li>)}
                </ul>
              </div>
            )}
            {p.exclusions?.length > 0 && (
              <div>
                <h3 className="font-display text-2xl text-slate-950 mb-4">Exclusions</h3>
                <ul className="space-y-2">
                  {p.exclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-slate-700"><X className="w-4 h-4 mt-1 text-red-500" /> {x}</li>)}
                </ul>
              </div>
            )}
          </section>

          {p.faqs?.length > 0 && (
            <section>
              <h3 className="font-display text-3xl text-slate-950 mb-6">FAQs</h3>
              <div className="divide-y divide-slate-200 border-t border-slate-200">
                {p.faqs.map((f, i) => (
                  <details key={i} className="py-5 group">
                    <summary className="cursor-pointer flex justify-between items-center list-none"><span className="font-display text-lg">{f.q}</span><span className="group-open:rotate-45 transition-transform">＋</span></summary>
                    <p className="mt-2 text-slate-600">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs uppercase tracking-[0.24em] text-amber-600">Enquire about this journey</div>
              <div className="mt-2 font-display text-2xl text-slate-950">Let&apos;s plan {p.name}</div>
              <p className="text-slate-500 text-sm mt-2">Available all year. Customisable dates and pace.</p>
              <div className="mt-6"><CTAButtons label={p.name} sourceRef={p.slug} testIdPrefix="pkg" /></div>
            </div>
            {related.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-3">Related packages</div>
                <ul className="space-y-3">
                  {related.filter((r) => r.slug !== p.slug).slice(0, 4).map((r) => (
                    <li key={r.id}><Link className="text-slate-800 hover:text-amber-700 text-sm font-medium" to={`/packages/${r.slug}`}>{r.name} · <span className="text-slate-500">{r.destination}</span></Link></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} className="max-h-[90vh] max-w-full rounded-lg" alt="" />
        </div>
      )}
    </div>
  );
}
